import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {FormGroupDirective} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {HarbourDuesBerthVisit, HarbourDuesRotterdamResponse, PurposeCode} from 'typescript-models/common';
import {CargoComponentMode} from '../../../details/berth-details/berth-visit/cargo/berth-visit-cargo-rotterdam.component'

// Cargo type code that represents container cargo
const CONTAINER_CARGO_TYPE = '0004';

@Component({
  selector: 'app-berth-visit-tabs-rotterdam',
  templateUrl: './berth-visit-tabs-rotterdam.component.html',
  styleUrl: './berth-visit-tabs-rotterdam.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BerthVisitTabsRotterdamComponent {
  protected readonly CargoComponentMode = CargoComponentMode;

  readonly berthVisitNumber = input.required<number>();
  readonly berthVisit = input.required<HarbourDuesBerthVisit>();
  readonly harbourDuesDetails = input.required<HarbourDuesRotterdamResponse>();
  readonly showValidationErrors = input<boolean>(false);

  // Optional so the component still renders in isolation (the tab-routing spec mounts it without a
  // parent form); in the app it is always inside the harbour-dues form.
  private readonly form = inject(FormGroupDirective, {optional: true});

  private readonly formValues = toSignal<{berthVisits: HarbourDuesBerthVisit[]}>(
    this.form?.control?.valueChanges ?? of(undefined),
    {initialValue: this.form?.control?.value}
  );

  private readonly selectedCargoTypesLoading = computed<string[]>(() => {
    const berthVisit = this.formValues()?.berthVisits?.at(this.berthVisitNumber());
    return (berthVisit?.loadedGoods || [])
      .map(goods => goods?.cargoType)
      .filter(cargoType => cargoType != null) as string[];
  });

  private readonly selectedCargoTypesDischarge = computed<string[]>(() => {
    const berthVisit = this.formValues()?.berthVisits?.at(this.berthVisitNumber());
    return (berthVisit?.dischargedGoods || [])
      .map(goods => goods?.cargoType)
      .filter(cargoType => cargoType != null) as string[];
  });

  protected readonly showContainerHandlingLoading = computed(
    () => this.isContainerCargoTypeSelected(this.selectedCargoTypesLoading())
  );

  protected readonly showContainerHandlingDischarge = computed(
    () => this.isContainerCargoTypeSelected(this.selectedCargoTypesDischarge())
  );

  private isContainerCargoTypeSelected(selectedCargoTypes: string[]): boolean {
    return selectedCargoTypes.includes(CONTAINER_CARGO_TYPE);
  }

  protected readonly hasLoadedGoods = computed(() =>
      this.berthVisit()?.visitPurposes?.some(
        value => value === PurposeCode.LOADING
      ) || false
  );

  protected readonly hasDischargedGoods = computed(() =>
      this.berthVisit()?.visitPurposes?.some(
        value => value === PurposeCode.DISCHARGE || value === PurposeCode.DISCHARGE_CRUDE_OIL
      ) || false
  );

  protected readonly showContainerTab = computed(
    () => this.showContainerHandlingLoading() || this.showContainerHandlingDischarge()
  );
}
